import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { CampaignProvider, useCampaign } from '../context/CampaignContext';
import { doc, getDoc, collection, getDocs, where, query, updateDoc, arrayRemove, addDoc, serverTimestamp, orderBy, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Gamepad2, Users, Settings, Copy, UserPlus, LogOut, FileText, BookOpen, Sparkles, ChevronRight, ChevronDown, Trash2, Clock } from 'lucide-react';
import GMDashboard from './campaign/GMDashboard';
import CampaignSettings from './campaign/CampaignSettings';
import RichTextEditor from './campaign/RichTextEditor';
import AppHeader from './layout/AppHeader';
import CampaignSettingsDropdown from './campaign/CampaignSettingsDropdown';
import CampaignCreatedModal from './CampaignCreatedModal';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { cn } from '../lib/utils';
import StickyVitalsBar from './character-sheet/StickyVitalsBar';
import { calculateEvasion, calculateArmorScore, calculateDamageThresholds, calculateProficiency } from '../utils/characterCalculations';
import { useDaggerheartData } from '../context/DataContext';

const CampaignView = ({ campaignId, isJoining = false }) => {
  const { campaignId: paramCampaignId } = useParams();
  const id = campaignId || paramCampaignId;

  if (!id) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400">Invalid campaign ID</p>
        </div>
      </div>
    );
  }

  return (
    <CampaignProvider campaignId={id}>
      <CampaignViewContent campaignId={id} isJoining={isJoining} />
    </CampaignProvider>
  );
};

const CampaignViewContent = ({ campaignId, isJoining = false }) => {
  const { campaignId: paramCampaignId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const id = campaignId || paramCampaignId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isConnected, connectionStatus, participants } = useCampaign();
  const [campaign, setCampaign] = useState(null);
  const [partyCharacters, setPartyCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGM, setIsGM] = useState(false);
  const [forcePlayerView, setForcePlayerView] = useState(false);
  const [userCharacters, setUserCharacters] = useState([]);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [activeTab, setActiveTab] = useState('notes'); // Default to notes tab
  const [notesSubTab, setNotesSubTab] = useState('archive'); // Session notes sub-tab: archive, summary
  const [currentSessionNotes, setCurrentSessionNotes] = useState('');
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [savingNotes, setSavingNotes] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState(null); // Track which archived note is expanded
  const [showCreatedModal, setShowCreatedModal] = useState(!!location.state?.justCreated);
  const isDesktop = useMediaQuery('(min-width: 1024px)'); // Desktop breakpoint
  const dataContext = useDaggerheartData();
  const gameData = dataContext?.gameData || null;

  // Helper function to prepare character data for StickyVitalsBar
  const prepareCharacterForVitalsBar = (character) => {
    // Normalize field names (currentHP -> hp, etc.)
    const normalizedChar = {
      ...character,
      hp: character.hitPoints?.current || character.hp || character.currentHP || 0,
      maxHp: character.hitPoints?.max || character.maxHp || character.maxHP || 8,
      stress: character.stress?.current || character.stress || character.currentStress || 0,
      maxStress: character.stress?.max || character.maxStress || character.maxStress || 8,
      hope: character.hope?.current || character.hope || character.currentHope || 0,
      traits: character.traits || {},
      conditions: character.conditions || [],
      scars: character.scars || [],
      loadout: character.loadout || [],
      armorMarks: character.armorMarks || []
    };

    // Get equipment info for calculations (with fallback if gameData not loaded)
    const armorInfo = gameData?.equipment && character.armor ? gameData.equipment[character.armor] : null;
    const secondaryWeaponInfo = gameData?.equipment && character.secondaryWeapon ? gameData.equipment[character.secondaryWeapon] : null;
    const classInfo = gameData?.classes?.[character.class];

    // Calculate combat stats (with safe fallbacks)
    const evasion = gameData?.equipment ? calculateEvasion(normalizedChar, armorInfo) : (character.evasion || 10);
    const armorScore = gameData?.equipment ? calculateArmorScore(armorInfo, secondaryWeaponInfo, normalizedChar) : (character.armorScore || 0);
    const thresholds = gameData?.equipment ? calculateDamageThresholds(armorInfo, character.level || 1, normalizedChar) : { minor: 3, major: 7, severe: 13 };
    const currentProficiency = gameData?.classes ? calculateProficiency(normalizedChar, classInfo) : (character.proficiency || 1);
    const spellcastTrait = classInfo?.spellcastTrait || null;

    return {
      character: normalizedChar,
      evasion,
      armorScore,
      thresholds,
      currentProficiency,
      spellcastTrait
    };
  };

  useEffect(() => {
    const loadCampaignData = async () => {
      if (!user || !id) {
        console.log('CampaignView: Missing user or id', { user: !!user, id });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Direct check of URL parameter - no memoization needed
        const viewParam = searchParams.get('view');
        const shouldForcePlayer = viewParam === 'player';
        
        // Always reset the state based on current URL
        setForcePlayerView(shouldForcePlayer);

        console.log('CampaignView: Loading campaign with id:', id, 'viewParam:', viewParam, 'shouldForcePlayer:', shouldForcePlayer);

        // Load campaign data
        const campaignDoc = await getDoc(doc(db, 'campaigns', id));
        if (!campaignDoc.exists()) {
          setError('Campaign not found');
          return;
        }

        const campaignData = { id: campaignDoc.id, ...campaignDoc.data() };

        // Clean up any duplicate players in the background BEFORE setting state
        if (campaignData.players && campaignData.players.length > 0) {
          // Remove duplicates based on userId and characterId
          const uniquePlayers = [];
          const seenUserIds = new Set();
          const seenCharacterIds = new Set();
          
          for (const player of campaignData.players) {
            if (!seenUserIds.has(player.userId) && !seenCharacterIds.has(player.characterId)) {
              uniquePlayers.push(player);
              seenUserIds.add(player.userId);
              seenCharacterIds.add(player.characterId);
            }
          }
          
          // Update local data immediately
          campaignData.players = uniquePlayers;
          
          // If we found duplicates, update the database silently in the background
          if (uniquePlayers.length !== campaignDoc.data().players?.length) {
            // Don't await this - let it happen in background
            updateDoc(doc(db, 'campaigns', id), {
              players: uniquePlayers
            }).catch(cleanupError => {
              console.error('Error cleaning up duplicate players:', cleanupError);
            });
          }
        }

        setCampaign(campaignData);

        // Validate essential campaign data
        if (!campaignData.gmId) {
          setError('Campaign data is corrupted - missing GM information');
          return;
        }

        // Check if user is GM
        const userIsGM = campaignData.gmId === user.uid;
        setIsGM(userIsGM);

        // Set default tab based on GM status
        if (userIsGM && !shouldForcePlayer) {
          setActiveTab('dashboard');
        } else {
          setActiveTab('notes');
        }

        // If user is GM and gmName is missing or generic, update it
        if (userIsGM && (!campaignData.gmName || campaignData.gmName === 'Game Master')) {
          const gmName = user.displayName || user.email || 'Game Master';
          try {
            await updateDoc(doc(db, 'campaigns', id), {
              gmName: gmName,
              updatedAt: new Date()
            });
            console.log('Updated campaign with GM name:', gmName);
            // Update local state
            setCampaign(prev => ({ ...prev, gmName }));
          } catch (error) {
            console.error('Error updating GM name:', error);
          }
        }

        // Check if user has access to this campaign
        const hasAccess = userIsGM || campaignData.players?.some(player => player.userId === user.uid);
        
        // If joining and user doesn't have access, show join modal
        if (isJoining && !hasAccess) {
          setShowJoinModal(true);
          // Load user's characters for joining
          const userCharactersQuery = query(
            collection(db, 'characters'),
            where('userId', '==', user.uid)
          );
          const userCharacterSnapshot = await getDocs(userCharactersQuery);
          const characters = userCharacterSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Filter out characters that are already in the campaign
          const availableCharacters = characters.filter(character => 
            !campaignData.players?.some(player => player.characterId === character.id)
          );
          
          setUserCharacters(availableCharacters);
        } else if (!hasAccess) {
          setError('You do not have access to this campaign');
          return;
        }

        // Load all characters for party members
        const characterPromises = [];
        const playerIds = campaignData.players?.map(player => player.userId).filter(Boolean) || [];
        const allUserIds = [campaignData.gmId, ...playerIds].filter(Boolean); // Filter out undefined values

        for (const userId of allUserIds) {
          if (userId) { // Double check userId is valid
            const userCharactersQuery = query(
              collection(db, 'characters'),
              where('userId', '==', userId)
            );
            characterPromises.push(getDocs(userCharactersQuery));
          }
        }

        const characterSnapshots = await Promise.all(characterPromises);
        const allCharacters = [];
        
        characterSnapshots.forEach(snapshot => {
          snapshot.forEach(doc => {
            allCharacters.push({ id: doc.id, ...doc.data() });
          });
        });

        // Filter to only characters that are in this campaign
        const campaignCharacters = allCharacters.filter(character => 
          campaignData.players?.some(player => player.characterId === character.id)
        );

        // Remove any potential duplicates in the character list (extra safety)
        const uniqueCampaignCharacters = campaignCharacters.filter((character, index, self) => 
          index === self.findIndex(c => c.id === character.id)
        );

        setPartyCharacters(uniqueCampaignCharacters);

        // Note: Removed automatic "viewing campaign" activity logging as it was too noisy
        // Activity logging is now reserved for meaningful actions like test activities, joining, leaving, etc.

      } catch (err) {
        console.error('Error loading campaign:', err);
        setError('Failed to load campaign data');
      } finally {
        setLoading(false);
      }
    };

    loadCampaignData();
  }, [user, id, searchParams.toString()]); // Use searchParams.toString() for stable dependency

  // Load session notes
  useEffect(() => {
    if (!id) return; // Need campaign ID

    // Load current session notes from campaign document (GM only)
    if (isGM) {
      const loadCurrentNotes = async () => {
        try {
          const campaignDoc = await getDoc(doc(db, 'campaigns', id));
          if (campaignDoc.exists()) {
            const data = campaignDoc.data();
            setCurrentSessionNotes(data.currentSessionNotes || '');
          }
        } catch (error) {
          console.error('Error loading current session notes:', error);
        }
      };

      loadCurrentNotes();
    }

    // Set up real-time listener for archived notes
    const archivedNotesQuery = query(
      collection(db, 'campaigns', id, 'archivedNotes'),
      orderBy('archivedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      archivedNotesQuery,
      (snapshot) => {
        const notes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setArchivedNotes(notes);
      },
      (error) => {
        console.error('Error loading archived notes:', error);
      }
    );

    return () => unsubscribe();
  }, [id, isGM]);

  const handleCharacterClick = (character) => {
    // In forced player view, GM can only access their own characters
    // In normal GM view, GM can access any character, players can only access their own
    const playerData = campaign.players?.find(player => player.characterId === character.id);
    const isOwnCharacter = character.userId === user.uid;
    const canAccess = forcePlayerView ? isOwnCharacter : (isGM || (playerData && playerData.userId === user.uid));

    if (canAccess) {
      // Navigate to character sheet (campaign auto-detected via useCharacterCampaign hook)
      navigate(`/character/${character.id}`);
    } else {
      toast.error('You can only view your own character sheet');
    }
  };

  const handleLeaveCampaign = async () => {
    if (!user || isGM) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to leave this campaign? This action cannot be undone.'
    );
    
    if (confirmed) {
      try {
        // Find the player's entry to remove
        const playerToRemove = campaign.players?.find(player => player.userId === user.uid);
        if (playerToRemove) {
          const campaignRef = doc(db, 'campaigns', id);
          await updateDoc(campaignRef, {
            players: arrayRemove(playerToRemove)
          });
          
          // Log the leave activity (commented out - logActivity not yet implemented)
          // TODO: Implement activity logging
          // if (logActivity) {
          //   logActivity('player_left', `${playerToRemove.playerName || 'A player'} left the campaign`, {
          //     playerName: playerToRemove.playerName || 'Unknown Player'
          //   });
          // }
          
          toast.success('You have left the campaign successfully.');
          navigate('/');
        }
      } catch (error) {
        console.error('Error leaving campaign:', error);
        toast.error('Failed to leave campaign. Please try again.');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Session Notes Handlers
  const handleSaveCurrentNotes = async () => {
    if (!id || !isGM) return;

    setSavingNotes(true);
    try {
      await updateDoc(doc(db, 'campaigns', id), {
        currentSessionNotes: currentSessionNotes,
        updatedAt: serverTimestamp()
      });
      toast.success('Session notes saved!');
    } catch (error) {
      console.error('Error saving session notes:', error);
      toast.error('Failed to save session notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleArchiveCurrentNotes = async () => {
    if (!id || !isGM || !currentSessionNotes.trim()) {
      toast.error('No notes to archive');
      return;
    }

    setSavingNotes(true);
    try {
      // Add to archived notes collection
      await addDoc(collection(db, 'campaigns', id, 'archivedNotes'), {
        content: currentSessionNotes,
        archivedAt: serverTimestamp(),
        archivedBy: user.displayName || user.email || 'GM'
      });

      // Clear current notes
      await updateDoc(doc(db, 'campaigns', id), {
        currentSessionNotes: '',
        updatedAt: serverTimestamp()
      });

      setCurrentSessionNotes('');
      toast.success('Notes archived successfully!');
      setNotesSubTab('archive'); // Switch to archive tab
    } catch (error) {
      console.error('Error archiving notes:', error);
      toast.error('Failed to archive notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteArchivedNote = async (noteId) => {
    if (!id || !isGM) return;

    const confirmed = window.confirm('Are you sure you want to delete this archived note? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'campaigns', id, 'archivedNotes', noteId));
      toast.success('Archived note deleted');
    } catch (error) {
      console.error('Error deleting archived note:', error);
      toast.error('Failed to delete archived note');
    }
  };

  // Toggle expand for archived note
  const handleToggleExpandNote = (noteId) => {
    setExpandedNoteId(expandedNoteId === noteId ? null : noteId);
  };

  // Auto-save archived note
  const handleSaveArchivedNote = async (noteId, content) => {
    if (!id || !noteId) return;

    try {
      const noteRef = doc(db, 'campaigns', id, 'archivedNotes', noteId);
      await updateDoc(noteRef, {
        content,
        lastEditedAt: new Date(),
        lastEditedBy: user.displayName || user.email
      });
    } catch (error) {
      console.error('Error updating archived note:', error);
      throw error; // Propagate to RichTextEditor for retry
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.08 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-100 mb-2">
            {error}
          </h3>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Return to Main Menu
          </button>
        </div>
      </div>
    );
  }

  // Join Modal - Updated to match modern UI
  if (showJoinModal) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-red-400 font-serif mb-3">
                Join Campaign
              </h1>
              <h2 className="text-2xl text-gray-100 mb-2">
                {campaign?.name}
              </h2>
              <p className="text-gray-400">
                Select a character to join this epic adventure
              </p>
            </div>

            {userCharacters.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 mb-4 opacity-30">
                  <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-100 mb-2">
                  No Characters Available
                </h3>
                <p className="text-gray-400 mb-6">
                  You need to create a character before joining a campaign.
                </p>
                <button
                  onClick={() => navigate('/create')}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105 shadow-lg font-semibold"
                >
                  Create New Character
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {userCharacters.map((character) => (
                    <div
                      key={character.id}
                      className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg p-6 cursor-pointer hover:border-red-600 transition-all transform hover:scale-105 hover:shadow-2xl"
                      onClick={async () => {
                        try {
                          // Get fresh campaign data to check current state
                          const campaignDoc = await getDoc(doc(db, 'campaigns', id));
                          if (!campaignDoc.exists()) {
                            toast.error('Campaign not found.');
                            return;
                          }
                          
                          const currentCampaignData = { id: campaignDoc.id, ...campaignDoc.data() };
                          
                          // Check if user or character is already in the campaign
                          const isUserAlreadyInCampaign = currentCampaignData.players?.some(player => 
                            player.userId === user.uid
                          );
                          
                          const isCharacterAlreadyInCampaign = currentCampaignData.players?.some(player => 
                            player.characterId === character.id
                          );
                          
                          if (isUserAlreadyInCampaign || isCharacterAlreadyInCampaign) {
                            toast.error('You are already part of this campaign or this character is already in the campaign.');
                            setShowJoinModal(false);
                            return;
                          }
                          
                          const campaignRef = doc(db, 'campaigns', id);
                          const playerData = {
                            userId: user.uid,
                            characterId: character.id,
                            playerName: user.displayName || user.email || 'Unknown Player',
                            joinedAt: new Date()
                          };
                          
                          // Remove any existing entries for this user/character before adding new one
                          const existingPlayers = currentCampaignData.players || [];
                          const cleanedPlayers = existingPlayers.filter(player => 
                            player.userId !== user.uid && player.characterId !== character.id
                          );
                          
                          // Add the new player data
                          const updatedPlayers = [...cleanedPlayers, playerData];
                          
                          await updateDoc(campaignRef, {
                            players: updatedPlayers
                          });
                          
                          // Log the join activity (commented out - logActivity not yet implemented)
                          // TODO: Implement activity logging
                          // if (logActivity) {
                          //   logActivity('player_joined', `${character.name} joined the campaign`, {
                          //     characterId: character.id,
                          //     characterName: character.name,
                          //     playerName: user.displayName || user.email || 'Unknown Player'
                          //   });
                          // }
                          
                          setShowJoinModal(false);
                          toast.success(`Successfully joined the campaign with ${character.name}!`);
                          
                          // Reload campaign data to show the updated state
                          window.location.reload();
                        } catch (error) {
                          console.error('Error joining campaign:', error);
                          toast.error('Failed to join campaign. Please try again.');
                        }
                      }}
                    >
                      <h3 className="text-xl font-bold text-gray-100 mb-2">
                        {character.name || 'Unnamed Character'}
                      </h3>
                      <p className="text-red-400 font-semibold mb-3">
                        Level {character.level || 1} {character.class || 'Unknown Class'}
                      </p>
                      {character.ancestry && (
                        <p className="text-sm text-gray-400 mb-2">
                          <span className="font-medium text-gray-300">Ancestry:</span> {Array.isArray(character.ancestry) ? character.ancestry.join(', ') : character.ancestry}
                        </p>
                      )}
                      {character.community && (
                        <p className="text-sm text-gray-400 mb-4">
                          <span className="font-medium text-gray-300">Community:</span> {character.community}
                        </p>
                      )}
                      <div className="text-center mt-4">
                        <span className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm rounded-lg font-semibold shadow-lg">
                          <UserPlus className="w-4 h-4" />
                          Select This Character
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center">
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-lg hover:from-gray-800 hover:to-gray-900 transition-all shadow-lg font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render party roster section (extracted for reuse)
  const renderPartyRoster = () => (
    <div className="p-4 lg:p-8">
      <h2 className="text-2xl font-bold text-gray-100 mb-6">
        Party Roster ({partyCharacters.length} {partyCharacters.length === 1 ? 'member' : 'members'})
      </h2>

      {partyCharacters.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Users className="w-16 h-16 mx-auto opacity-30" />
          </div>
          <h3 className="text-lg font-semibold text-gray-100 mb-2">
            No party members yet
          </h3>
          <p className="text-gray-400">
            Players need to join this campaign with their characters to form the party.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partyCharacters.map((character) => {
            const playerData = campaign.players?.find(player => player.characterId === character.id);
            const isOwnCharacter = character.userId === user.uid;
            const canAccess = forcePlayerView ? isOwnCharacter : (isGM || isOwnCharacter);

            return (
              <div
                key={character.id}
                className={cn(
                  "bg-gradient-to-br from-gray-900 to-black border rounded-lg p-6 transition-all",
                  canAccess
                    ? 'hover:border-red-600 cursor-pointer border-red-700'
                    : 'opacity-75 cursor-not-allowed border-gray-700'
                )}
                onClick={() => handleCharacterClick(character)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-100">
                      {character.name || 'Unnamed Character'}
                    </h3>
                    <p className="text-gray-400">
                      Level {character.level || 1} {character.class || 'Unknown Class'}
                    </p>
                    <p className="text-sm text-blue-400 font-medium mt-1">
                      {isOwnCharacter ? '👤 Your Character' : `👤 ${playerData?.playerName || 'Unknown Player'}`}
                    </p>
                  </div>
                  <div className="text-right">
                    {canAccess ? (
                      <span className="text-green-400">
                        👁️
                      </span>
                    ) : (
                      <span className="text-gray-600">
                        🚫
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {character.ancestry && (
                    <p className="text-sm text-gray-400">
                      <span className="font-medium">Ancestry:</span> {Array.isArray(character.ancestry) ? character.ancestry.join(', ') : character.ancestry}
                    </p>
                  )}
                  {character.community && (
                    <p className="text-sm text-gray-400">
                      <span className="font-medium">Community:</span> {character.community}
                    </p>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  {canAccess ? (
                    (isGM && !forcePlayerView) ? 'Click to view/edit character' : 'Click to view character'
                  ) : (
                    'View restricted'
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-background min-h-screen text-gray-100">
      {/* Header Banner - Fixed at top (matching TabbedCharacterSheet) */}
      <AppHeader
        pageActions={
          <CampaignSettingsDropdown
            campaignId={id}
            isGM={isGM}
            forcePlayerView={forcePlayerView}
            onSwitchToGMView={() => navigate(`/campaign/${id}`)}
            onJoinAsPlayer={async () => {
              try {
                const userCharactersQuery = query(
                  collection(db, 'characters'),
                  where('userId', '==', user.uid)
                );
                const userCharacterSnapshot = await getDocs(userCharactersQuery);
                const characters = userCharacterSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const availableCharacters = characters.filter(character =>
                  !campaign.players?.some(player => player.characterId === character.id)
                );
                if (availableCharacters.length === 0) {
                  toast.error('All your characters are already in this campaign or you have no characters to join with.');
                  return;
                }
                setUserCharacters(availableCharacters);
                setShowJoinModal(true);
              } catch (error) {
                console.error('Error loading characters:', error);
                toast.error('Failed to load characters. Please try again.');
              }
            }}
            onLeaveCampaign={handleLeaveCampaign}
            onOpenChronicleSetup={() => {
              setActiveTab('settings');
              setTimeout(() => {
                document.getElementById('discord-integration')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          />
        }
      />

      {/* Campaign Container - Padding for fixed header */}
      <div className="min-h-screen pt-[73px]">
        {/* Desktop Sidebar - Matching DesktopSidebar.js patterns exactly */}
        {isDesktop && (
          <aside
            className="hidden lg:flex flex-col w-sidebar bg-surface-1 fixed left-0 top-[73px] bottom-0 z-40 shadow-2xl"
            role="navigation"
            aria-label="Campaign navigation"
          >
            {/* Campaign Identity Card - Matching pattern from DesktopSidebar */}
            <div className="px-4 py-4 space-y-2">
              {/* Campaign Name */}
              <h2 className="text-heading-lg text-red-400 font-bold truncate font-serif">
                {campaign?.name || 'Campaign'}
              </h2>

              {/* Campaign Details */}
              <div className="text-sm space-y-1 text-gray-300">
                {/* Campaign Frame & Status */}
                <div className="font-medium">
                  <span className="text-gray-100">
                    {dataContext?.campaignFrames?.[campaign?.campaignFrame]?.name || (campaign?.campaignFrame === 'core' || !campaign?.campaignFrame ? 'Core Rules' : campaign.campaignFrame)}
                  </span>
                  {' • '}
                  {isGM && !forcePlayerView ? (
                    <span className="text-purple-400">👑 GM View</span>
                  ) : (
                    <span className="text-green-400">🎲 Player View</span>
                  )}
                </div>

                {/* Players Count */}
                <div className="text-gray-400 text-xs">
                  {partyCharacters.length} {partyCharacters.length === 1 ? 'party member' : 'party members'}
                </div>
              </div>
            </div>

            {/* Navigation Items - Matching DesktopSidebar pattern */}
            <nav className="flex-1 py-2 px-3" role="tablist">
              {isGM && !forcePlayerView && (
                <button
                  role="tab"
                  aria-selected={activeTab === 'dashboard'}
                  aria-controls="dashboard-panel"
                  onClick={() => setActiveTab('dashboard')}
                  className={cn(
                    // Base styles - Google Photos spacing (more generous)
                    'w-full flex items-center gap-4 px-4 py-4 mb-1',
                    'text-left transition-all duration-200',
                    'relative rounded-lg',
                    // Focus styles (WCAG AA)
                    'focus:outline-none focus:ring-2 focus:ring-purple-700 focus:ring-inset',
                    // Active/hover states with purple for GM
                    activeTab === 'dashboard'
                      ? 'text-purple-400 bg-purple-900/20 border border-purple-700/50'
                      : 'text-gray-400 hover:text-purple-300 hover:bg-purple-900/10'
                  )}
                >
                  {/* Active indicator (left border) with purple accent for GM */}
                  {activeTab === 'dashboard' && (
                    <div
                      className="absolute left-0 top-1 bottom-1 w-1 bg-purple-700 rounded-r-full shadow-lg shadow-purple-700/50"
                      aria-hidden="true"
                    />
                  )}

                  {/* Icon - Larger for Google Photos feel (28px) */}
                  <Gamepad2 className="w-7 h-7 flex-shrink-0" />

                  {/* Label */}
                  <span className="text-nav font-medium">GM Dashboard</span>
                </button>
              )}

              {/* Party Roster Tab - Only visible to non-GM players */}
              {(!isGM || forcePlayerView) && (
                <button
                  role="tab"
                  aria-selected={activeTab === 'party'}
                  aria-controls="party-panel"
                  onClick={() => setActiveTab('party')}
                  className={cn(
                    // Base styles - Google Photos spacing (more generous)
                    'w-full flex items-center gap-4 px-4 py-4 mb-1',
                    'text-left transition-all duration-200',
                    'relative rounded-lg',
                    // Focus styles (WCAG AA)
                    'focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-inset',
                    // Active/hover states with blue accent
                    activeTab === 'party'
                      ? 'text-blue-400 bg-blue-900/20 border border-blue-700/50'
                      : 'text-gray-400 hover:text-blue-300 hover:bg-blue-900/10'
                  )}
                >
                  {/* Active indicator (left border) with blue accent */}
                  {activeTab === 'party' && (
                    <div
                      className="absolute left-0 top-1 bottom-1 w-1 bg-blue-700 rounded-r-full shadow-lg shadow-blue-700/50"
                      aria-hidden="true"
                    />
                  )}

                  {/* Icon - Larger for Google Photos feel (28px) */}
                  <Users className="w-7 h-7 flex-shrink-0" />

                  {/* Label */}
                  <span className="text-nav font-medium">Party</span>
                </button>
              )}

              <button
                role="tab"
                aria-selected={activeTab === 'notes'}
                aria-controls="notes-panel"
                onClick={() => setActiveTab('notes')}
                className={cn(
                  // Base styles - Google Photos spacing (more generous)
                  'w-full flex items-center gap-4 px-4 py-4 mb-1',
                  'text-left transition-all duration-200',
                  'relative rounded-lg',
                  // Focus styles (WCAG AA)
                  'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                  // Active/hover states with red gaming aesthetics
                  activeTab === 'notes'
                    ? 'text-red-400 bg-red-900/20 border border-red-700/50'
                    : 'text-gray-400 hover:text-red-300 hover:bg-red-900/10'
                )}
              >
                {/* Active indicator (left border) with red accent */}
                {activeTab === 'notes' && (
                  <div
                    className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
                    aria-hidden="true"
                  />
                )}

                {/* Icon - Larger for Google Photos feel (28px) */}
                <FileText className="w-7 h-7 flex-shrink-0" />

                {/* Label */}
                <span className="text-nav font-medium">Session Notes</span>
              </button>

              {isGM && !forcePlayerView && (
                <button
                  role="tab"
                  aria-selected={activeTab === 'settings'}
                  aria-controls="settings-panel"
                  onClick={() => setActiveTab('settings')}
                  className={cn(
                    // Base styles - Google Photos spacing (more generous)
                    'w-full flex items-center gap-4 px-4 py-4 mb-1',
                    'text-left transition-all duration-200',
                    'relative rounded-lg',
                    // Focus styles (WCAG AA)
                    'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                    // Active/hover states
                    activeTab === 'settings'
                      ? 'text-red-400 bg-red-900/20 border border-red-700/50'
                      : 'text-gray-400 hover:text-red-300 hover:bg-red-900/10'
                  )}
                >
                  {/* Active indicator (left border) */}
                  {activeTab === 'settings' && (
                    <div
                      className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
                      aria-hidden="true"
                    />
                  )}

                  {/* Icon - Larger for Google Photos feel (28px) */}
                  <Settings className="w-7 h-7 flex-shrink-0" />

                  {/* Label */}
                  <span className="text-nav font-medium">Campaign Settings</span>
                </button>
              )}
            </nav>

            {/* Connection Status - Bottom */}
            <div className="px-3 py-3 border-t border-gray-700">
              <div className="flex items-center gap-2 px-4 py-2">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  connectionStatus === 'connected' ? 'bg-green-400 animate-pulse' :
                  connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' :
                  'bg-red-400'
                )} />
                <span className="text-sm text-gray-400">
                  {connectionStatus === 'connected' ? `${participants.length} online` :
                   connectionStatus === 'connecting' ? 'Connecting...' :
                   'Offline'}
                </span>
              </div>
            </div>
          </aside>
        )}

        {/* Main Content Area */}
        <main className={cn(
          "min-w-0 max-w-full pb-20 lg:pb-0 bg-background",
          isDesktop && "lg:ml-sidebar"
        )}>
          {/* Tab Content */}
          {/* GM Dashboard Tab */}
          {activeTab === 'dashboard' && isGM && !forcePlayerView && (
            <GMDashboard campaign={campaign} characters={partyCharacters} />
          )}

          {/* Party Roster Tab - Available to all players */}
          {activeTab === 'party' && (
            <div className="p-4 lg:p-8">
              <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-blue-700">
                <h2 className="text-xl font-bold mb-3 text-blue-400 font-serif flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Party Roster
                  <span className="text-sm text-gray-500">({partyCharacters.length} members)</span>
                </h2>

                {partyCharacters.length > 0 ? (
                  <div className="space-y-3">
                    {partyCharacters.map(character => {
                      const vitalsData = prepareCharacterForVitalsBar(character);

                      if (!vitalsData) return null;

                      return (
                        <div
                          key={character.id}
                          onClick={() => navigate(`/character/${character.id}`)}
                          className="relative bg-[#282828] border border-gray-600 rounded-lg hover:bg-[#2C2C2C] hover:border-blue-500 transition-all cursor-pointer overflow-hidden"
                        >
                          {/* Character Name & Info */}
                          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-gray-100">{character.name}</span>
                              <span className="text-xs text-gray-500">-</span>
                              <span className="text-xs text-gray-400">
                                Level {character.level || 1} {character.class?.charAt(0).toUpperCase() + character.class?.slice(1)}
                                {character.subclass && `: ${character.subclass.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
                              </span>
                              {(character.ancestry || character.community) && (
                                <>
                                  <span className="text-xs text-gray-500">-</span>
                                  <span className="text-xs text-gray-400">
                                    {(() => {
                                      // Handle ancestry (string, object, or array for mixed ancestry)
                                      let ancestryStr = '';
                                      if (typeof character.ancestry === 'string') {
                                        ancestryStr = character.ancestry;
                                      } else if (Array.isArray(character.ancestry)) {
                                        // Mixed ancestry - join all with /
                                        ancestryStr = character.ancestry.map(a =>
                                          a.charAt(0).toUpperCase() + a.slice(1)
                                        ).join('/');
                                      } else if (character.ancestry?.name) {
                                        ancestryStr = character.ancestry.name;
                                      }

                                      // Handle community (string, object, or array)
                                      let communityStr = '';
                                      if (typeof character.community === 'string') {
                                        communityStr = character.community;
                                      } else if (Array.isArray(character.community)) {
                                        communityStr = character.community.map(c =>
                                          c.charAt(0).toUpperCase() + c.slice(1)
                                        ).join('/');
                                      } else if (character.community?.name) {
                                        communityStr = character.community.name;
                                      }

                                      // Build final display
                                      const parts = [];
                                      if (ancestryStr) parts.push(ancestryStr.charAt(0).toUpperCase() + ancestryStr.slice(1));
                                      if (communityStr) parts.push(communityStr.charAt(0).toUpperCase() + communityStr.slice(1));
                                      return parts.join(' - ');
                                    })()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* StickyVitalsBar (modified to be inline, not fixed) */}
                          <div className="[&>div]:!static [&>div]:!top-auto [&>div]:!left-auto [&>div]:!right-auto [&>div]:!z-auto [&>div]:!rounded-none [&>div]:!overflow-visible [&>div]:bg-transparent [&>div]:!w-auto [&>div]:!max-w-none [&>div]:!p-0">
                            <StickyVitalsBar
                              character={vitalsData.character}
                              companion={null}
                              updateVitals={() => {}} // No-op (read-only for players)
                              toggleCondition={() => {}} // No-op (read-only for players)
                              hpDamageMode="normal"
                              toggleHPDamageMode={() => {}} // No-op (read-only for players)
                              currentProficiency={vitalsData.currentProficiency}
                              evasion={vitalsData.evasion}
                              armorScore={vitalsData.armorScore}
                              thresholds={vitalsData.thresholds}
                              markArmor={() => {}} // No-op (read-only for players)
                              spellcastTrait={vitalsData.spellcastTrait}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No party members yet</p>
                    {isGM && !forcePlayerView ? (
                      <>
                        <p className="text-xs text-gray-600 mt-1">Share your invite link so players can join</p>
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/join-campaign/${campaignId}`;
                            navigator.clipboard.writeText(link).then(() => {
                              toast.success('Invite link copied!');
                            }).catch(() => {
                              toast.error('Failed to copy link');
                            });
                          }}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-900/40 text-red-300 rounded-md hover:bg-red-900/60 border border-red-700 transition-all duration-200 text-sm font-medium"
                        >
                          <Copy className="w-4 h-4" />
                          Copy Invite Link
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-gray-600 mt-1">Waiting for players to join the campaign</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session Notes Tab */}
          {activeTab === 'notes' && (
            <div className="p-4 lg:p-8">
              <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700">
                <h2 className="text-xl font-bold mb-3 text-red-400 font-serif">Session Notes</h2>

                {/* Sub-Tab Navigation */}
                <div className="flex border-b border-red-700 mb-3">
                  <button
                    onClick={() => setNotesSubTab('archive')}
                    className={`flex-1 flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 py-2 text-sm font-medium transition-colors duration-200 relative ${
                      notesSubTab === 'archive'
                        ? 'text-red-400'
                        : 'text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    <BookOpen className="w-5 h-5" />
                    <span className="text-xs sm:text-sm">Archive</span>
                    {notesSubTab === 'archive' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-400" />
                    )}
                  </button>
                  <button
                    onClick={() => setNotesSubTab('summary')}
                    className={`flex-1 flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 py-2 text-sm font-medium transition-colors duration-200 relative ${
                      notesSubTab === 'summary'
                        ? 'text-red-400'
                        : 'text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    <Sparkles className="w-5 h-5" />
                    <span className="text-xs sm:text-sm">Summary</span>
                    {notesSubTab === 'summary' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-400" />
                    )}
                  </button>
                </div>

                {/* Sub-Tab Content */}
                <div className="min-h-[300px]">
                  {/* Archived Notes */}
                  {notesSubTab === 'archive' && (
                    <div className="space-y-4">
                      {archivedNotes.length === 0 ? (
                        <div className="text-center py-12">
                          <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-sm text-gray-400">No archived notes yet</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {isGM && !forcePlayerView
                              ? 'Write session notes during play, then archive them when the session ends.'
                              : "Your GM hasn't archived any session notes yet. Check back after your next session!"}
                          </p>
                        </div>
                      ) : (
                        archivedNotes.map((note) => {
                          const noteContent = note.content || ''

                          // Extract session title from H1 tag or use title field
                          const h1Match = noteContent.match(/<h1>(.*?)<\/h1>/)
                          const sessionTitle = h1Match ? h1Match[1] : (note.title || 'Archived Session')

                          // Strip HTML tags and create clean preview
                          const stripHtml = (html) => {
                            // Remove GM notes (spans with data-gm-note attribute)
                            let cleaned = html.replace(/<span[^>]*data-gm-note="true"[^>]*>.*?<\/span>/gi, '')
                            // Remove all HTML tags
                            cleaned = cleaned.replace(/<[^>]+>/g, ' ')
                            // Decode HTML entities
                            cleaned = cleaned.replace(/&nbsp;/g, ' ')
                                             .replace(/&amp;/g, '&')
                                             .replace(/&lt;/g, '<')
                                             .replace(/&gt;/g, '>')
                                             .replace(/&quot;/g, '"')
                            // Remove excessive whitespace
                            cleaned = cleaned.replace(/\s+/g, ' ').trim()
                            return cleaned
                          }

                          // Get preview text (skip H1, truncate to ~150 chars)
                          const contentWithoutTitle = noteContent.replace(/<h1>.*?<\/h1>/i, '')
                          const previewText = stripHtml(contentWithoutTitle)
                          const truncatedPreview = previewText.length > 150
                            ? previewText.substring(0, 150) + '...'
                            : previewText

                          const isExpanded = expandedNoteId === note.id;

                          // Extract session stats
                          const duration = note.duration || 0;
                          const wordCount = note.wordCount || 0;

                          return (
                            <div key={note.id} className="bg-gray-800 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors">
                              {/* Header */}
                              <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div
                                    className="flex items-start gap-2 flex-1 cursor-pointer"
                                    onClick={() => handleToggleExpandNote(note.id)}
                                  >
                                    <div className="flex-1">
                                      <h3 className="text-base font-semibold text-red-400 mb-1">
                                        {sessionTitle}
                                      </h3>
                                      <p className="text-xs text-gray-400 mb-2">
                                        Archived {formatDate(note.archivedAt)} by {note.archivedBy}
                                      </p>
                                      {/* Session Stats */}
                                      <div className="flex flex-wrap gap-3 text-xs">
                                        {duration > 0 && (
                                          <div className="flex items-center gap-1 text-blue-400">
                                            <Clock className="w-3 h-3" />
                                            <span>{duration} min</span>
                                          </div>
                                        )}
                                        {wordCount > 0 && (
                                          <div className="flex items-center gap-1 text-green-400">
                                            <FileText className="w-3 h-3" />
                                            <span>{wordCount.toLocaleString()} words</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    {isGM && !forcePlayerView && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteArchivedNote(note.id);
                                        }}
                                        className="text-red-400 hover:text-red-300 p-2 rounded hover:bg-red-900/20 transition-colors"
                                        title="Delete archived note"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {/* Preview (when not expanded) */}
                                {!isExpanded && truncatedPreview && (
                                  <p
                                    className="text-sm text-gray-300 line-clamp-3 cursor-pointer hover:text-white transition-colors mt-2"
                                    onClick={() => handleToggleExpandNote(note.id)}
                                  >
                                    {truncatedPreview}
                                  </p>
                                )}
                              </div>

                              {/* Expanded View - Auto-saving Editor */}
                              {isExpanded && (
                                <div className="px-4 pb-4 border-t border-gray-700">
                                  <RichTextEditor
                                    content={note.content || ''}
                                    onChange={(content) => handleSaveArchivedNote(note.id, content)}
                                    onSave={(content) => handleSaveArchivedNote(note.id, content)}
                                    isGM={true}
                                    placeholder="Edit archived session notes..."
                                    autoSave={true}
                                    autoSaveDelay={2000}
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}

                  {/* Campaign Summary (Future AI Feature) */}
                  {notesSubTab === 'summary' && (
                    <div className="text-center py-12">
                      <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">Campaign Summary</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Coming soon! Automatically analyze your session notes to generate campaign summaries, track NPCs, plot threads, and more.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && isGM && !forcePlayerView && (
            <CampaignSettings
              campaign={campaign}
              characters={partyCharacters}
              onUpdate={() => {
                // Reload campaign data
                const loadData = async () => {
                  try {
                    const campaignDoc = await getDoc(doc(db, 'campaigns', id));
                    if (campaignDoc.exists()) {
                      setCampaign({ id: campaignDoc.id, ...campaignDoc.data() });
                    }
                  } catch (error) {
                    console.error('Error reloading campaign:', error);
                  }
                };
                loadData();
              }}
            />
          )}
        </main>

        {/* Mobile Bottom Tab Bar */}
        {!isDesktop && (
          <nav className="fixed bottom-0 left-0 right-0 bg-surface-1 border-t border-gray-700 z-40">
            <div className="flex items-center justify-around min-h-[56px] pb-safe">
              {isGM && !forcePlayerView && (
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-1 py-2",
                    activeTab === 'dashboard' && "text-purple-400"
                  )}
                >
                  <Gamepad2 className={cn("w-5 h-5", activeTab === 'dashboard' && "scale-110")} />
                  <span className="text-xs">Dashboard</span>
                </button>
              )}

              {/* Party tab - only for non-GM players */}
              {(!isGM || forcePlayerView) && (
                <button
                  onClick={() => setActiveTab('party')}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-1 py-2",
                    activeTab === 'party' && "text-blue-400"
                  )}
                >
                  <Users className={cn("w-5 h-5", activeTab === 'party' && "scale-110")} />
                  <span className="text-xs">Party</span>
                </button>
              )}

              <button
                onClick={() => setActiveTab('notes')}
                className={cn(
                  "flex-1 flex flex-col items-center gap-1 py-2",
                  activeTab === 'notes' && "text-red-400"
                )}
              >
                <FileText className={cn("w-5 h-5", activeTab === 'notes' && "scale-110")} />
                <span className="text-xs">Notes</span>
              </button>

              {isGM && !forcePlayerView && (
                <button
                  onClick={() => setActiveTab('settings')}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-1 py-2",
                    activeTab === 'settings' && "text-red-400"
                  )}
                >
                  <Settings className={cn("w-5 h-5", activeTab === 'settings' && "scale-110")} />
                  <span className="text-xs">Settings</span>
                </button>
              )}
            </div>
          </nav>
        )}
      </div>

      {showCreatedModal && campaign && (
        <CampaignCreatedModal
          campaign={campaign}
          onClose={() => setShowCreatedModal(false)}
        />
      )}
    </div>
  );
};

export default CampaignView;
